import { useState, useEffect, useRef } from "react";
import { toSlug } from "utils";
import styles from "./Onboarding.module.css";
import onboardingImage from "assets/images/onboading-video-placeholder.jpg";
import youtube_video from "../assets/images/youtube.svg";
import tick_mark from "../assets/images/check-mark.svg";
import StickyHeader from "./StickyHeader";
import { Link, useParams } from "react-router-dom";
import { Navigate } from "react-router-dom";

export default function Onboarding({ data }) {
  const { slug } = useParams();
  const activeTab =
    data.find((tab) => `${toSlug(tab.name)}` === slug.toLowerCase()) || data[0];
  const [expanded, setExpanded] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(0); // Track which dropdown is open
  const videoRef = useRef(null);

  useEffect(() => {
    setOpenDropdown(activeTab);
    // Scroll to the section after component mounts/updates
    if (activeTab) {
      const HEADER_OFFSET = 90;
      const sectionId = `section-${toSlug(activeTab.name)}`;
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) {
          const elementTop = el.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementTop - HEADER_OFFSET;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
      }, 100); // Small delay to ensure DOM is ready
    }
  }, [activeTab]);

  const handleStartVideo = () => {
    setExpanded(true); // expand after attempting play
  };

  // when collapsing, pause and reset
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (expanded) {
      v.muted = true; // required for reliable autoplay
      v.playsInline = true; // iOS
      // wait a frame so CSS visibility applies
      requestAnimationFrame(() => {
        v.play().catch(() => {});
      });
    } else {
      try {
        v.pause();
      } catch {}
      v.currentTime = 0;
    }
  }, [expanded]);

  useEffect(() => {
    document.title = activeTab.title;
  }, [activeTab]);

  const handleDropdownToggle = (index) => {
    setOpenDropdown(openDropdown === index ? -1 : index);
  };

  const renderContent = (item) => (
    <article
      className={styles.contentWrapper}
      id={`section-${toSlug(item.name)}`}
    >
      <section className={styles.sectionOneWrapper}>
        <header className={styles.headerContainer}>
          <h1 className={styles.header}>{item.data.title}</h1>
        </header>

        <div className={styles.textContainer}>
          <h2 className={styles.sectionOneHeader}>{item.data.subTitle}</h2>
          <p className={styles.sectionOneDescription}>
            {item.data.description}
          </p>
          <p className={styles.sectionOneSubText}>{item.data.subText}</p>

          <div className={styles.sectionOneStepsContainer}>
            <ol className={styles.sectionOneStepWrapper}>
              {item.data.steps.map((step, i) => (
                <li
                  className={styles.sectionOneStepListWrapper}
                  key={`step-${i}`}
                >
                  <span className={styles.sectionOneStep}>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section
        className={styles.sectionTwoWrapper}
        aria-labelledby="why-icredentialing"
      >
        <div className={styles.sectionTwoTextContainer}>
          <h2 id="why-icredentialing" className={styles.sectionTwoHeader}>
            Why iCredentialing is the Right Choice for You
          </h2>
          <p className={styles.sectionTwoDescription}>
            Our unique methodology ensures accuracy, efficiency, and compliance,
            reducing administrative burdens while improving timeliness and
            reimbursement cycles.
          </p>
        </div>

        <div className={styles.sectionTwoVideoWrapper}>
          <figure className={styles.sectionTwoVideoContainer}>
            <figcaption className="sr-only">
              Video: Why iCredentialing is the Right Choice for You
            </figcaption>

            <img
              src={onboardingImage}
              className={`${styles.sectionTwoVideo} ${
                expanded ? styles.hidden : ""
              }`}
              alt="iCredentialing onboarding process overview showing automated credentialing workflow"
              loading="lazy"
            />

            <button
              onClick={handleStartVideo}
              className={`${styles.sectionTwoVideoOverlay} ${
                expanded ? styles.hidden : ""
              }`}
              aria-label="Play video about iCredentialing benefits"
              type="button"
            >
              <div className={styles.sectionTwoVideoPlayButton}>
                <img
                  src={youtube_video}
                  className={styles["sectionTwoVideoPlayButtonImage"]}
                  alt=""
                  aria-hidden="true"
                />
              </div>
              <span className={styles.sectionTwoVideoOverlayText}>
                Watch 2:00 min video
              </span>
            </button>

            <video
              ref={videoRef}
              className={`${styles.sectionTwoVideo} ${styles.videoPlayable} ${
                expanded ? "" : styles.collapsed
              }`}
              controls
              autoPlay
              muted
              playsInline
              preload="auto"
              aria-label="iCredentialing benefits and features overview"
              onCanPlay={() => {
                const v = videoRef.current;
                if (expanded && v) v.play().catch(() => {});
              }}
            >
              <source
                src="https://ihs-frontend-videos-and-assets.s3.us-east-2.amazonaws.com/onboarding.mp4"
                type="video/mp4"
              />
              <p>
                Your browser doesn't support HTML5 video. Here is a{" "}
                <a href="https://ihs-frontend-videos-and-assets.s3.us-east-2.amazonaws.com/onboarding.mp4">
                  link to the video
                </a>{" "}
                instead.
              </p>
            </video>

            {expanded && (
              <button
                className={styles.sectionTwoVideoCloseButton}
                onClick={() => setExpanded(false)}
                aria-label="Close video player"
                type="button"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </figure>

          <ul className={styles.sectionTwoCardContainer}>
            {item.data.cardInfo.map((benefit, i) => (
              <li className={styles.sectionTwoCardWrapper} key={`benefit-${i}`}>
                <img
                  src={tick_mark}
                  className={styles["sectionTwoVideoPlayButtonImage"]}
                  alt=""
                  aria-hidden="true"
                />
                <p className={styles.sectionTwoCardText}>{benefit}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </article>
  );

  if (!activeTab) {
    return <Navigate to={`/services/${toSlug(data[0].name)}`} replace />;
  }

  return (
    <main className={styles.container}>
      <title>{activeTab.title}</title>
      <meta name="description" content={activeTab.description} />
      <meta
        name="keywords"
        content="credentialing, provider onboarding, healthcare compliance, automated credentialing"
      />
      <link
        rel="canonical"
        href={`https://icredentialing.com/${toSlug(activeTab.name)}`}
      />

      <div className={styles.onboardingContentWrapper}>
        <header className={styles.onboardingHeaderContainer}>
          <div className={styles.onboardingHeaderTextContainer}>
            <p className={styles.onboardingHeaderText}>
              At iCredentialing, we specialize in automated, AI-driven
              onboarding, credentialing, and compliance solutions - streamlining
              provider enrollment and continuous monitoring through our secure
              AWS cloud-based platform for unmatched speed, accuracy, and
              compliance.
            </p>
          </div>
          <small className={styles.Credit}>Designed by FreePik</small>
        </header>

        <StickyHeader childClass={styles.subheaderContentWrapper}>
          <nav
            className={styles.subheaderContentWrapper}
            aria-label="Credentialing services navigation"
          >
            <ul className={styles.onboardingNavMenuContainer}>
              {data.map((ele) => (
                <li
                  key={toSlug(ele.name)}
                  className={styles.onboardingNavMenuItemsButtonContainer}
                >
                  <Link
                    to={`/services/${toSlug(ele.name)}`}
                    // style={styles.onboardingNavMenuContainerLink}
                    className={`${styles.onboardingNavMenuItems} ${
                      ele.name === activeTab.name && styles.selected
                    }`}
                    aria-current={
                      ele.name === activeTab.name ? "page" : undefined
                    }
                  >
                    <img
                      src={
                        ele.name === activeTab.name ? ele.activeIcon : ele.icon
                      }
                      className={styles.subHeaderIcon}
                      alt=""
                      aria-hidden="true"
                    />
                    <span>{ele.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </StickyHeader>

        <div className={styles.desktopContent}>
          {data.map((item) => renderContent(item))}
        </div>

        <div className={styles.mobileContent}>
          {data.map((item, index) => (
            <section
              key={toSlug(item.name)}
              className={styles.accordionItem}
              aria-labelledby={`accordion-${toSlug(item.name)}`}
            >
              <h2>
                <button
                  className={`${styles.accordionHeader} ${
                    openDropdown === index ? styles.accordionHeaderActive : ""
                  }`}
                  onClick={() => handleDropdownToggle(index)}
                  aria-expanded={openDropdown === index}
                  aria-controls={`accordion-content-${toSlug(item.name)}`}
                  id={`accordion-${toSlug(item.name)}`}
                  type="button"
                >
                  <span className={styles.accordionHeaderContent}>
                    <img
                      src={openDropdown === index ? item.activeIcon : item.icon}
                      className={styles.accordionIcon}
                      alt=""
                      aria-hidden="true"
                    />
                    <span className={styles.accordionTitle}>{item.name}</span>
                  </span>
                  <span
                    className={`${styles.accordionArrow} ${
                      openDropdown === index ? styles.accordionArrowRotated : ""
                    }`}
                    aria-hidden="true"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
              </h2>

              <div
                id={`accordion-content-${toSlug(item.name)}`}
                className={`${styles.accordionContent} ${
                  openDropdown === index ? styles.accordionContentOpen : ""
                }`}
                role="region"
                aria-labelledby={`accordion-${toSlug(item.name)}`}
                hidden={openDropdown !== index}
              >
                {renderContent(item)}
              </div>
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}
